import { useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { useTranslationApp } from "../../lib/i18next";
import LanguageSwitcher from "../../components/Common/LanguageSwitcher";
import {
  normalizeAppLanguage,
  persistAppLanguage,
} from "../../utils/i18nLanguage";
import {
  AI_COMPETITORS_ANALYZING_DESCRIPTION,
  AI_COMPETITORS_ANALYZING_MUTED,
  AI_COMPETITORS_ANALYZING_STEP_NUMBER,
  AI_COMPETITORS_ANALYZING_TITLE,
  AI_COMPETITORS_PAGE_TITLE,
  AI_COMPETITORS_PROGRESS_CONSOLIDATING_ADS,
  AI_COMPETITORS_PROGRESS_CONSOLIDATING_COMPETITORS,
  AI_COMPETITORS_PROGRESS_FINDING_COMPETITORS,
  AI_COMPETITORS_PROGRESS_RESOLVING_ADS,
  AI_COMPETITORS_PROGRESS_REVIEWING_PAGE,
  AI_COMPETITORS_PROGRESS_SEARCHING_ADS,
  AI_COMPETITORS_SEARCHING_MUTED,
  AI_COMPETITORS_SEARCHING_STEP_NUMBER,
  AI_COMPETITORS_SEARCHING_TITLE,
} from "../../i18n/keysTranslations";
import AiCompetitorsHeader from "./components/AiCompetitorsHeader";
import AiCompetitorsUrlStep from "./components/AiCompetitorsUrlStep";
import AiCompetitorsProgressStep from "./components/AiCompetitorsProgressStep";
import AiCompetitorsCompetitorsStep from "./components/AiCompetitorsCompetitorsStep";
import AiCompetitorsResultsStep from "./components/AiCompetitorsResultsStep";
import { useAiCompetitorsWizard } from "./hooks/useAiCompetitorsWizard";
import { useAiCompetitorsWarmup } from "./hooks/useAiCompetitorsWarmup";
import { useAiCompetitorsSessionListener } from "./hooks/useAiCompetitorsSessionListener";
import {
  AI_COMPETITORS_ADS_SEARCH_STAGES,
  AI_COMPETITORS_ANALYSIS_STAGES,
  useAiCompetitorsStageProgress,
} from "./hooks/useAiCompetitorsStageProgress";
import {
  AI_COMPETITORS_STEP_ANALYZING,
  AI_COMPETITORS_STEP_RESULTS,
  AI_COMPETITORS_STEP_SEARCHING,
} from "./aiCompetitorsConstants";
import { captureAiCompetitorsLandingQueryParams } from "./aiCompetitorsLandingQuery";

const LANGUAGE_SWITCHER_STYLES = {
  position: "absolute",
  top: { xs: 12, sm: 40 },
  right: { xs: 24, sm: 16 },
  zIndex: 2,
};

const LANGUAGE_SWITCHER_BUTTON_STYLES = {
  minWidth: "max-content",
  border: "none !important",
  boxShadow: "none !important",
  backgroundColor: "transparent !important",
  px: "0 !important",
  "&.MuiButtonBase-root.MuiButton-root:hover": {
    backgroundColor: "transparent !important",
    border: "none !important",
    boxShadow: "none !important",
    color: "inherit !important",
  },
};

const LANGUAGE_SWITCHER_MENU_STYLES = {
  "& .MuiPaper-root": {
    marginTop: "8px",
  },
};

const getStepMaxWidth = (step, adsCount = 0) => {
  if (step === AI_COMPETITORS_STEP_RESULTS && adsCount > 0) {
    return "1120px";
  }
  return "640px";
};

const AiCompetitors = () => {
  const { t, i18n } = useTranslationApp();
  const [searchParams] = useSearchParams();
  const currentLanguage = normalizeAppLanguage(i18n.language) || "en";

  useEffect(() => {
    captureAiCompetitorsLandingQueryParams(searchParams);
  }, [searchParams]);

  const {
    sessionId,
    step,
    steps,
    websiteUrl,
    isFoundRoute,
    competitors,
    ads,
    adsSummary,
    urlError,
    searchError,
    hasSnapshot,
    setCompetitors,
    onAnalyzeWebsite,
    onSearchAds,
    onGoToAgenda,
    onMissingFoundSession,
    onSessionSnapshot,
    setSearchError,
  } = useAiCompetitorsWizard({ language: currentLanguage });

  useAiCompetitorsWarmup();
  const isProgressStep =
    step === AI_COMPETITORS_STEP_ANALYZING ||
    step === AI_COMPETITORS_STEP_SEARCHING;

  const liveSession = useAiCompetitorsSessionListener({
    sessionId,
    language: currentLanguage,
    isActive: isProgressStep || isFoundRoute,
    fetchOnce: true,
    createIfMissing: false,
    pollMs: isProgressStep ? 450 : 0,
    onNotFound: onMissingFoundSession,
  });

  useEffect(() => {
    onSessionSnapshot(liveSession);
  }, [liveSession, onSessionSnapshot]);

  const onChangeLanguage = useCallback(
    (language) => {
      const nextLanguage = normalizeAppLanguage(language);
      if (!nextLanguage || nextLanguage === currentLanguage) {
        return;
      }
      i18n.changeLanguage(nextLanguage);
      persistAppLanguage(nextLanguage);
    },
    [currentLanguage, i18n]
  );

  const analysisTicker = useAiCompetitorsStageProgress({
    stages: AI_COMPETITORS_ANALYSIS_STAGES,
    status: liveSession?.analysis?.status,
    copy: liveSession?.analysis?.copy,
    backendProgress: liveSession?.analysis?.progress,
    fallbackCopyByStatus: {
      analyzing_website: t(AI_COMPETITORS_PROGRESS_REVIEWING_PAGE),
      finding_competitors: t(AI_COMPETITORS_PROGRESS_FINDING_COMPETITORS),
      preparing_competitors: t(
        AI_COMPETITORS_PROGRESS_CONSOLIDATING_COMPETITORS
      ),
    },
    isActive: step === AI_COMPETITORS_STEP_ANALYZING,
  });
  const adsTicker = useAiCompetitorsStageProgress({
    stages: AI_COMPETITORS_ADS_SEARCH_STAGES,
    status: liveSession?.adsSearch?.status,
    copy: liveSession?.adsSearch?.copy,
    backendProgress: liveSession?.adsSearch?.progress,
    fallbackCopyByStatus: {
      resolving: t(AI_COMPETITORS_PROGRESS_RESOLVING_ADS),
      searching: t(AI_COMPETITORS_PROGRESS_SEARCHING_ADS),
      preparing: t(AI_COMPETITORS_PROGRESS_CONSOLIDATING_ADS),
    },
    isActive: step === AI_COMPETITORS_STEP_SEARCHING,
  });

  return (
    <>
      <Helmet>
        <title>{t(AI_COMPETITORS_PAGE_TITLE)}</title>
      </Helmet>
      <Box sx={{ position: "relative" }}>
        <Box sx={LANGUAGE_SWITCHER_STYLES}>
          <LanguageSwitcher
            language={currentLanguage}
            onChangeLanguage={onChangeLanguage}
            colorButton="secondary"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={LANGUAGE_SWITCHER_BUTTON_STYLES}
            sxMenu={LANGUAGE_SWITCHER_MENU_STYLES}
          />
        </Box>
        <Box
          component="main"
          sx={{
            minHeight: "100dvh",
            bgcolor: "background.default",
            color: "text.primary",
            overflowX: "hidden",
            px: { xs: 3, sm: 3 },
            pt: { xs: 1.25, sm: 3 },
            pb: { xs: "max(24px, env(safe-area-inset-bottom))", sm: 6 },
          }}
        >
          <Box
            component="section"
            sx={{
              mx: "auto",
              display: "flex",
              width: "100%",
              maxWidth: getStepMaxWidth(
                step,
                adsSummary?.adsCount ?? ads.length
              ),
              flexDirection: "column",
            }}
          >
            <AiCompetitorsHeader
              compact
              sx={{ mb: { xs: 1, sm: 2 }, pr: { xs: 6, sm: 0 } }}
            />
            <Fade in key={step} timeout={500}>
              <Box>
                {!isFoundRoute && step === steps.url ? (
                  <AiCompetitorsUrlStep
                    initialUrl={websiteUrl}
                    onSubmit={onAnalyzeWebsite}
                    errorCode={urlError}
                  />
                ) : null}
                {!isFoundRoute && step === steps.analyzing ? (
                  <AiCompetitorsProgressStep
                    stepNumber={t(AI_COMPETITORS_ANALYZING_STEP_NUMBER)}
                    title={t(AI_COMPETITORS_ANALYZING_TITLE)}
                    mutedTitle={t(AI_COMPETITORS_ANALYZING_MUTED)}
                    description={t(AI_COMPETITORS_ANALYZING_DESCRIPTION)}
                    progress={analysisTicker.progress}
                    label={analysisTicker.label}
                  />
                ) : null}
                {isFoundRoute && !hasSnapshot && !searchError ? (
                  <AiCompetitorsProgressStep
                    stepNumber={t(AI_COMPETITORS_ANALYZING_STEP_NUMBER)}
                    title={t(AI_COMPETITORS_ANALYZING_TITLE)}
                    mutedTitle={t(AI_COMPETITORS_ANALYZING_MUTED)}
                    description={t(AI_COMPETITORS_ANALYZING_DESCRIPTION)}
                    progress={analysisTicker.progress}
                    label={analysisTicker.label}
                  />
                ) : null}
                {step === steps.competitors &&
                (!isFoundRoute || hasSnapshot) ? (
                  <AiCompetitorsCompetitorsStep
                    competitors={competitors}
                    onChangeCompetitors={setCompetitors}
                    onSearchAds={onSearchAds}
                  />
                ) : null}
                {step === steps.searching && (!isFoundRoute || hasSnapshot) ? (
                  <AiCompetitorsProgressStep
                    stepNumber={t(AI_COMPETITORS_SEARCHING_STEP_NUMBER)}
                    title={t(AI_COMPETITORS_SEARCHING_TITLE)}
                    mutedTitle={t(AI_COMPETITORS_SEARCHING_MUTED)}
                    progress={adsTicker.progress}
                    label={adsTicker.label}
                    error={searchError}
                    onRetry={() => {
                      setSearchError(false);
                      onSearchAds();
                    }}
                  />
                ) : null}
                {hasSnapshot && step === steps.results ? (
                  <AiCompetitorsResultsStep
                    competitors={competitors}
                    ads={ads}
                    adsSummary={adsSummary}
                    onGoToAgenda={onGoToAgenda}
                  />
                ) : null}
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AiCompetitors;
